<template>
  <div class="tw-p-1">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
      <div class="tw-flex tw-items-center tw-gap-2">
        <h3 class="tw-text-base tw-font-semibold tw-text-slate-900">Notes</h3>
        <span
          v-if="Array.isArray(notes)"
          class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-black tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-white"
        >
          {{ notes.length }}
        </span>
      </div>
    </div>

    <div v-if="Array.isArray(notes) && notes.length > 0" class="tw-mt-4 tw-divide-y tw-divide-slate-200/70 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/50">
      <div
        v-for="(note, idx) in notes"
        :key="note.id || idx"
        class="tw-p-3"
      >
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div class="tw-flex tw-items-center tw-gap-2">
            <div class="tw-text-xs tw-font-medium tw-text-slate-500">{{ note.date }}</div>
            <span
              v-if="typeof note.isPublic === 'boolean'"
              class="tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold"
              :class="note.isPublic ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-slate-200 tw-text-slate-700'"
            >
              {{ note.isPublic ? 'Printable' : 'Internal' }}
            </span>
          </div>
          <div>
            <button
              v-if="container || order || trip || customer"
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
              @click="startEditNote(note)"
              :disabled="isUpdatingNote || isArchivingNote"
            >
              Edit
            </button>
            <button
              v-if="container || order || trip || customer"
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-rose-700 hover:tw-bg-rose-100"
              @click="archiveNote(note)"
              :disabled="isUpdatingNote || isArchivingNote"
            >
              {{ isArchivingNote ? 'Archiving...' : 'Archive' }}
            </button>
          </div>
        </div>

        <div v-if="editingNoteId === note.id" class="tw-mt-2 tw-space-y-2">
          <textarea
            v-model="editingMessage"
            class="tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-sm tw-leading-6 tw-text-slate-900 focus:tw-outline-none"
            rows="4"
            :disabled="isUpdatingNote"
          ></textarea>
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <label
              v-if="container || order || trip"
              class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700"
            >
              <input
                v-model="editingIsPublic"
                type="checkbox"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
                :disabled="isUpdatingNote"
              />
              Printable Note
            </label>
            <div class="tw-flex tw-items-center tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
                @click="cancelEditNote"
                :disabled="isUpdatingNote"
              >
                Cancel
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-transparent tw-bg-slate-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60"
                @click="saveEditedNote(note)"
                :disabled="!editingMessage || isUpdatingNote"
              >
                {{ isUpdatingNote ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
        <div v-else class="tw-mt-1.5 tw-text-sm tw-leading-6 tw-text-slate-800 tw-whitespace-pre-wrap">{{ note.message }}</div>
      </div>
    </div>
    <div v-else class="tw-mt-4 tw-text-sm tw-text-slate-500">No notes yet.</div>

    <div v-if="archivedNotes && archivedNotes.length > 0" class="tw-mt-4">
      <button
        type="button"
        class="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-white"
        @click="showArchived = !showArchived"
      >
        <font-awesome-icon :icon="showArchived ? 'chevron-up' : 'chevron-down'" class="tw-h-3 tw-w-3" />
        Archived Notes ({{ archivedNotes.length }})
      </button>

      <div v-if="showArchived" class="tw-mt-3 tw-divide-y tw-divide-slate-200/70 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-slate-50/50">
        <div
          v-for="(note, idx) in archivedNotes"
          :key="note.id || idx"
          class="tw-p-3 tw-opacity-70"
        >
          <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div class="tw-flex tw-items-center tw-gap-2">
              <div class="tw-text-xs tw-font-medium tw-text-slate-500">{{ note.date }}</div>
              <span class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-slate-200 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-slate-600">
                Archived
              </span>
            </div>
            <button
              v-if="container || order || trip || customer"
              type="button"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
              @click="unarchiveNote(note)"
              :disabled="isArchivingNote"
            >
              Unarchive
            </button>
          </div>
          <div class="tw-mt-1.5 tw-text-sm tw-leading-6 tw-text-slate-600 tw-whitespace-pre-wrap">{{ note.message }}</div>
        </div>
      </div>
    </div>

    <div class="tw-mt-5">
      <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-p-3 focus-within:tw-ring-2 focus-within:tw-ring-indigo-500/20">
        <textarea
          v-model="message"
          class="tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-1 tw-py-1.5 tw-text-sm tw-leading-6 tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none"
          rows="4"
          placeholder="Add a note…"
          :disabled="isAddingNote"
        ></textarea>

        <div class="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <label
            v-if="container || order || trip"
            class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700"
          >
            <input
              v-model="isPublic"
              type="checkbox"
              class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300"
              :disabled="isAddingNote"
            />
            Printable Note
          </label>
          <button
            class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-900/20 disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            type="button"
            v-on:click="addNote"
            :disabled="!message || isAddingNote"
          >
            <span v-if="isAddingNote" class="tw-mr-2" uk-spinner></span>
            {{ isAddingNote ? 'Adding...' : 'Add Note' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AddContainerNotesMutation from '@/gql/notes/AddContainerNote.gql';
import AddOrderNoteMutation from '@/gql/notes/AddOrderNote.gql';
import AddTripNoteMutation from '@/gql/notes/AddTripNote.gql';
import AddCustomerNotesMutation from '@/gql/notes/AddCustomerNote.gql';
import UpdateNoteMutation from '@/gql/notes/UpdateNote.gql';
import ArchiveNoteMutation from '@/gql/notes/ArchiveNote.gql';
import UpdateCustomerNoteMutation from '@/gql/notes/UpdateCustomerNote.gql';
import ArchiveCustomerNoteMutation from '@/gql/notes/ArchiveCustomerNote.gql';
import UnarchiveNoteMutation from '@/gql/notes/UnarchiveNote.gql';
import UnarchiveCustomerNoteMutation from '@/gql/notes/UnarchiveCustomerNote.gql';
import ContainerQuery from '@/gql/containers/Container.gql';

type NoteT = {
  id: number | string;
  date: string;
  message: string;
  isPublic?: boolean;
};

export default defineComponent({
  name: 'AddNotesArea',
  props: ['notes', 'archivedNotes', 'container', 'customer', 'type', 'order', 'trip'],
  data() {
    return {
      message: '',
      isPublic: false,
      isAddingNote: false,
      editingNoteId: null as null | number | string,
      editingMessage: '',
      editingIsPublic: false,
      isUpdatingNote: false,
      isArchivingNote: false,
      showArchived: false,
    };
  },
  methods: {
    startEditNote(note: NoteT) {
      const vm = this as any;
      vm.editingNoteId = note.id;
      vm.editingMessage = note.message || '';
      vm.editingIsPublic = !!note.isPublic;
    },
    cancelEditNote() {
      const vm = this as any;
      vm.editingNoteId = null;
      vm.editingMessage = '';
      vm.editingIsPublic = false;
    },
    saveEditedNote(note: NoteT) {
      const vm = this as any;
      if (!note?.id || vm.isUpdatingNote || !vm.editingMessage?.trim()) {
        return;
      }

      vm.isUpdatingNote = true;
      vm.$apollo
        .mutate({
          mutation: vm.customer ? UpdateCustomerNoteMutation : UpdateNoteMutation,
          variables: {
            noteId: note.id,
            note: vm.editingMessage.trim(),
            isPublic: vm.container || vm.order || vm.trip ? vm.editingIsPublic : undefined,
          },
        })
        .then((result: any) => {
          const notes = vm.customer
            ? result?.data?.updateCustomerNote?.notes || []
            : result?.data?.updateNote?.notes || [];
          vm.$emit('addedNote', notes);
          vm.cancelEditNote();
        })
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          vm.isUpdatingNote = false;
        });
    },
    archiveNote(note: NoteT) {
      const vm = this as any;
      if (!note?.id || vm.isArchivingNote || vm.isUpdatingNote) {
        return;
      }
      if (!confirm('Archive this note?')) {
        return;
      }

      vm.isArchivingNote = true;
      vm.$apollo
        .mutate({
          mutation: vm.customer ? ArchiveCustomerNoteMutation : ArchiveNoteMutation,
          variables: {
            noteId: note.id,
          },
        })
        .then((result: any) => {
          const payload = vm.customer
            ? result?.data?.archiveCustomerNote
            : result?.data?.archiveNote;
          const notes = payload?.notes || [];
          const archivedNotes = payload?.archivedNotes || [];
          vm.$emit('addedNote', notes);
          vm.$emit('archivedNotesUpdated', archivedNotes);
          if (vm.editingNoteId === note.id) {
            vm.cancelEditNote();
          }
        })
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          vm.isArchivingNote = false;
        });
    },
    unarchiveNote(note: NoteT) {
      const vm = this as any;
      if (!note?.id || vm.isArchivingNote) {
        return;
      }

      vm.isArchivingNote = true;
      vm.$apollo
        .mutate({
          mutation: vm.customer ? UnarchiveCustomerNoteMutation : UnarchiveNoteMutation,
          variables: {
            noteId: note.id,
          },
        })
        .then((result: any) => {
          const payload = vm.customer
            ? result?.data?.unarchiveCustomerNote
            : result?.data?.unarchiveNote;
          const notes = payload?.notes || [];
          const archivedNotes = payload?.archivedNotes || [];
          vm.$emit('addedNote', notes);
          vm.$emit('archivedNotesUpdated', archivedNotes);
        })
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          vm.isArchivingNote = false;
        });
    },
    addNote() {
      const vm = (this as any);

      if (vm.message === '' || vm.isAddingNote) {
        return;
      }

      if (vm.container) {
        vm.isAddingNote = true;
        vm.$apollo
          .mutate({
            mutation: AddContainerNotesMutation,
            variables: {
              note: vm.message,
              containerId: Number(vm.container.id),
              isPublic: vm.isPublic,
            },
            awaitRefetchQueries: true,
            refetchQueries: [
              {
                query: ContainerQuery,
                variables: {
                  id: Number(vm.container.id),
                },
              },
            ],
          })
          .then((data) => {
            vm.$emit('addedNote', data.data.addNote.notes);
            vm.message = '';
            vm.isPublic = false;
          })
          .catch((error) => {
            vm.message = '';
            console.error(error);
          })
          .finally(() => {
            vm.isAddingNote = false;
          });
      } else if (vm.order) {
        vm.isAddingNote = true;
        vm.$apollo
          .mutate({
            mutation: AddOrderNoteMutation,
            variables: {
              note: vm.message,
              orderId: Number(vm.order.id),
              isPublic: vm.isPublic,
            },
          })
          .then((data) => {
            const notes = 
              (data as any)?.data?.addOrderNote?.notes || // @Dmtryo: why both???
              (data as any)?.data?.addNote?.notes ||
              [];
            vm.$emit('addedNote', notes);
            vm.message = '';
            vm.isPublic = false;
            console.log(data);
          })
          .catch((error) => {
            vm.message = '';
            console.error(error);
          })
          .finally(() => {
            vm.isAddingNote = false;
          });
      } else if (vm.trip) {
        vm.isAddingNote = true;
        vm.$apollo
          .mutate({
            mutation: AddTripNoteMutation,
            variables: {
              note: vm.message,
              tripId: Number(vm.trip.id),
              isPublic: vm.isPublic,
            },
          })
          .then((data) => {
            const notes = (data as any)?.data?.addTripNote?.notes || [];
            vm.$emit('addedNote', notes);
            vm.message = '';
            vm.isPublic = false;
          })
          .catch((error) => {
            vm.message = '';
            console.error(error);
          })
          .finally(() => {
            vm.isAddingNote = false;
          });
      } else if (vm.customer) {
        vm.isAddingNote = true;
        vm.$apollo
          .mutate({
            mutation: AddCustomerNotesMutation,
            variables: {
              note: vm.message,
              customerId: Number(vm.customer.id),
            },
          })
          .then((data) => {
            const notes = (data as any)?.data?.addCustomerNote?.notes || [];
            vm.$emit('addedNote', notes);
            vm.message = '';
            vm.isPublic = false;
          })
          .catch((error) => {
            vm.message = '';
            console.error(error);
          })
          .finally(() => {
            vm.isAddingNote = false;
          });
        console.log('Making Customer notes for customer');
      }
    },
  },
});
</script>

<style>
</style>
